import { createContext, useContext, useMemo } from 'react';
import { separateContexts } from '../../common/filtering';
import { AnalyticsContext } from './AnalyticsContext';
import { AppAccessContext } from './AppAccessContext';
import { QuestionsAndAnswersContext, RatingContext, SurveyContext } from './AppAdminContext';
import { AppDataContext } from './AppDataContext';
import { AppFormsContext } from './AppFormsContext';
import { AppNotificationsContext } from './AppNotificationsContext';
import { AppParentContext } from './AppParentContext';
import { AppContext } from './AppProvider';
import { AppSettingsContext } from './AppSettingsContext';
import { AppUserContext } from './AppUserContext';
import { AppUsersAuthContext } from './AppUsersAuthContext';
import { AppUsersContext } from './AppUsersContext';
import { AuthContext } from './AuthContext';
import { ClientInfoContext } from './ClientInfoContext';
import { DatabaseContext } from './DatabaseContext';
import { DataWrapperContext } from './DataWrapperContext';
import { EventInfoContext } from './EventInfoContext';
import { FunctionsContext } from './FunctionsContext';
import { HomeInfoContext } from './HomeInfoContext';
import { LogContext } from './LogContext';
import { ManifestContext } from './ManifestContext';
import { NavigationContext } from './NavigationContext';
import { PageContext } from './PageContext';
import { PapsContext } from './PapsContext';
import { PreviewContext } from './PreviewContext';
import { SearchParamContext } from './SearchParamContext';
import { ServiceWorkerContext } from './ServiceWorkerContext';
import { ClientSettingsContext, CurrentSettingsContext, EventSettingsContext, HomeSettingsContext } from './SettingsDataContext';
import { StartContext } from './StartContext';
import { StartDataContext } from './StartDataContext';
import { StorageContext } from './StorageContext';
import { TransitionContext } from './TransitionContext';
import { ValidationContext } from './ValidationContext';
import { VideoConferenceContext } from './VideoConferenceContext';
import { BaseSettingsContext } from './BaseSettingsContext';
import { RegDataContext } from './RegDataContext';
import { DemoContext } from './DemoContext';
import { AppTimestampContext } from './AppTimestampContext';
import { FavoritesContext } from './FavoritesContext';

/**
 * Provider providing...
  appAccessContext
  , `appFormsContext`
  , `appNotificationsContext`
  , `appSettingsContext` 
  , `authContext` 
  , `clientSettingsContext` 
  , `appUserDataContext` 
  , `dataWrapperContext` 
  , `eventSettingsContext` 
  , `homeSettingsContext`
  , `manifestContext`
  , `pageContext`
  , `papsContext` 
  , `startContext`
  , `startDataContext`
  , `eventInfoContext`
  , `storageContext`
  , `videoConferenceContext`
 */
export const ParentContext = createContext();

const ParentProvider = (props) => {

  const appTimestampContext = useContext(AppTimestampContext);
  const analyticsContext = useContext(AnalyticsContext);
  const appAccessContext = useContext(AppAccessContext);
  const appContext = useContext(AppContext);
  const appDataContext = useContext(AppDataContext);
  const appFormsContext = useContext(AppFormsContext);
  const appNotificationsContext = useContext(AppNotificationsContext);
  const appParentContext = useContext(AppParentContext);
  const appSettingsContext = useContext(AppSettingsContext)
  const appUserAuthContext = useContext(AppUsersAuthContext)
  const appUserContext = useContext(AppUserContext)
  const appUsersContext = useContext(AppUsersContext)
  const authContext = useContext(AuthContext);
  const baseSettingsContext = useContext(BaseSettingsContext);
  const clientInfoContext = useContext(ClientInfoContext);
  const clientSettingsContext = useContext(ClientSettingsContext);
  const currentSettingsContext = useContext(CurrentSettingsContext);
  const databaseContext = useContext(DatabaseContext)
  const dataWrapperContext = useContext(DataWrapperContext)
  const demoContext = useContext(DemoContext)
  const eventInfoContext = useContext(EventInfoContext);
  const eventSettingsContext = useContext(EventSettingsContext);
  const favoritesContext = useContext(FavoritesContext);
  const functionsContext = useContext(FunctionsContext);
  const homeInfoContext = useContext(HomeInfoContext)
  const homeSettingsContext = useContext(HomeSettingsContext);
  const logContext = useContext(LogContext)
  const manifestContext = useContext(ManifestContext);
  const navigationContext = useContext(NavigationContext);
  const pageContext = useContext(PageContext);
  const papsContext = useContext(PapsContext);
  const previewContext = useContext(PreviewContext)
  const questionsAndAnswersContext = useContext(QuestionsAndAnswersContext);
  const ratingContext = useContext(RatingContext);
  const regDataContext = useContext(RegDataContext);
  const searchParamContext = useContext(SearchParamContext);
  const serviceWorkerContext = useContext(ServiceWorkerContext);
  const startContext = useContext(StartContext);
  const startDataContext = useContext(StartDataContext);
  const storageContext = useContext(StorageContext);
  const surveyContext = useContext(SurveyContext);
  const transitionContext = useContext(TransitionContext)
  const validationContext = useContext(ValidationContext)
  const videoConferenceContext = useContext(VideoConferenceContext);

  const contexts = {
    analyticsContext,
    appTimestampContext,
    appAccessContext,
    appContext,
    appDataContext,
    appFormsContext,
    appNotificationsContext,
    appParentContext,
    appSettingsContext,
    appUserAuthContext,
    appUserContext,
    appUsersContext,
    authContext,
    baseSettingsContext,
    clientInfoContext,
    clientSettingsContext,
    currentSettingsContext,
    databaseContext,
    dataWrapperContext,
    demoContext,
    eventInfoContext,
    eventSettingsContext,
    favoritesContext,
    functionsContext,
    homeInfoContext,
    homeSettingsContext,
    logContext,
    manifestContext,
    navigationContext,
    pageContext,
    papsContext,
    previewContext,
    questionsAndAnswersContext,
    ratingContext,
    regDataContext,
    searchParamContext,
    serviceWorkerContext,
    startContext,
    startDataContext,
    storageContext,
    surveyContext,
    transitionContext,
    validationContext,
    videoConferenceContext,
    // appUsersContext,
  }

  const parent_contexts = useMemo(() => separateContexts(contexts), [contexts]);

  const { states, settings } = parent_contexts
  const { eventSettings, clientSettings, homeSettings } = settings ?? {}

  const { global: global_h } = homeSettings ?? {}
  const { global: global_c } = clientSettings ?? {}
  const { global: global_e } = eventSettings ?? {}

  // Start by creating a copy of global_e to avoid direct mutation
  let updatedGlobalE = { ...global_e };

  // Check if parentDefaults exist and modify updatedGlobalE accordingly
  if (eventSettings && eventSettings.global && eventSettings.global.parentDefaults) {
    const { useClientThemes, useClientUiElements, useClientUiSections } = global_e.parentDefaults;

    // Modify theme-related settings if required
    if (useClientThemes) {
      if (global_c && global_c.themeColors) {
        updatedGlobalE.themeColors = global_c.themeColors; // Update themeColors
      }
      if (global_c && global_c.themedItems) {
        updatedGlobalE.themedItems = global_c.themedItems; // Update themedItems
      }
    }

    // Modify UI elements if required
    if (useClientUiElements) {
      if (global_c && global_h.uiElements) {
        updatedGlobalE.uiElements = global_c.uiElements; // Update uiElements
      }
    }

    // Modify UI sections if required
    if (useClientUiSections) {
      if (global_c && global_c.uiSections) {
        updatedGlobalE.uiSections = global_c.uiSections; // Update uiSections
      }
    }
  }

  const updatedParentContexts = {
    ...parent_contexts,
    settings: {
      ...parent_contexts.settings,
      eventSettings: {
        ...parent_contexts.settings?.eventSettings,
        global: updatedGlobalE, // Set the updated global settings in eventSettings
      },
    },
  };

  // if (eventSettings && eventSettings.global && eventSettings.global.parentDefaults) {
  //   const { useClientThemes, useClientUiElements, useClientUiSections } = global_e.parentDefaults
  //   if (useClientThemes) {
  //     if (global_c && global_c.themeColors) { global_e.themeColors = global_c.themeColors }
  //     if (global_c && global_c.themedItems) { global_e.themedItems = global_c.themedItems }
  //   }
  //   if (useClientUiElements) {
  //     if (global_c && global_h.uiElements) { global_e.uiElements = global_c.uiElements }
  //   }
  //   if (useClientUiSections) {
  //     if (global_c && global_c.uiSections) { global_e.uiSections = global_c.uiSections }
  //   }
  // }

  // const [parent_contextz, setParent_contextz] = useState()

  const validateApp = () => {
    const { currentSettings_state, manifest_state } = states
    const { aps_global, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
    const { manifest, topMenu } = aps_global ?? {}
    const { appUserCollection } = aps_appUserSettings ?? {}
    const { startIcon, manifestIcons } = manifest_state ?? {}
    const { mainFixedCaption } = topMenu ?? {}
    const { appleStartUpImage, appleTouchIcon72, appleTouchIcon120, appleTouchIcon152, name: name_manifest, shortName } = manifest ?? {}

    const appUserOk = appUserCollection ? true : false
    const menuOk = mainFixedCaption ? true : false
    const manifestOk = manifestIcons && Object.keys(manifestIcons).length === 8 && startIcon ? true : false
    const manifestIOk = (appleStartUpImage && appleTouchIcon72 && appleTouchIcon120 && appleTouchIcon152 && name_manifest && shortName) ? true : false

    if (appUserOk && menuOk && manifestOk && manifestIOk) {
      return true
    }
  }

  const parent_fns = {
    addToContexts: (c) => {
      let { handlers } = parent_contexts
      handlers = { ...handlers, ...c }
      // setParent_contextz(parent_contexts)
    }
  }

  return <ParentContext.Provider value={{ ...props, validApp: validateApp(), ...updatedParentContexts, parent_fns }}>
    {props.children}
  </ParentContext.Provider>
};

export default ParentProvider